import { useContext, useMemo, useReducer } from 'react'

import { useQuery, useZero } from '@rocicorp/zero/react'

import { DayContext } from 'src/lib/dayContext'
import { NativeObjectTypes, PropertyTypes } from 'src/lib/objects'
import type { Schema } from 'src/zero/schema'

import { buildObjects } from '../Objects/build'
import useObjects from '../Objects/useObjects'

import PipelinesContext from './PipelinesContext'
import { pipelinesReducer } from './reducer'

const PipelineSkeletonPropertyIds = [
  'title',
  'ownerEmail',
  'stageId',
  'pipelineId',
  'position',
  'domain',
  'type',
  'primaryContactEmail',
  'expectedRevenue',
  'currency',
  'likelihoodToClose',
  'currentSituation',
  'exists',
]

const PipelinesProvider = ({ children }) => {
  const { propertyDefinitions } = useObjects()
  const { selectedWorkspace: workspaceId } = useContext(DayContext)

  const z = useZero<Schema>()

  const [pipelineRowsResultsZero, result] = useQuery(
    z.query.objectProperty.where(({ and, cmp, not }) =>
      and(
        cmp('workspaceId', workspaceId),
        cmp('userId', ''),
        not(cmp('propertyTypeId', PropertyTypes.DeleteMarker)),
        cmp('objectTypeId', 'IN', [
          NativeObjectTypes.Pipeline,
          NativeObjectTypes.Opportunity,
          NativeObjectTypes.Stage,
        ]),
        cmp('name', 'IN', PipelineSkeletonPropertyIds)
      )
    ),
    {
      // Temporarily disabled while we look further into query performance
      enabled: false, // !!workspaceId,
    }
  )
  const [_state, _dispatch] = useReducer(pipelinesReducer, null)

  const objects = useMemo(() => {
    return buildObjects(
      workspaceId,
      (pipelineRowsResultsZero || []) as any[],
      Object.values(propertyDefinitions)
    )
  }, [pipelineRowsResultsZero, workspaceId, propertyDefinitions])

  const value = useMemo(() => {
    if (!result) {
      return {}
    }

    return {
      workspaceId,
      pipelines: objects.filter(
        (object) => object.objectType === NativeObjectTypes.Pipeline
      ),
      opportunities: objects.filter(
        (object) => object.objectType === NativeObjectTypes.Opportunity
      ),
      stages: objects.filter(
        (object) => object.objectType === NativeObjectTypes.Stage
      ),
    }
  }, [workspaceId, objects, result])

  return (
    <PipelinesContext.Provider value={value}>
      {children}
    </PipelinesContext.Provider>
  )
}

export default PipelinesProvider
